import { Badge, Group, Text, type UseTreeReturnType } from '@mantine/core';
import type { RefObject } from 'react';
import { MethodBadge, TreeNodeToggle } from './ExplorerParts';
import { compareModules } from './module-filter';
import type { ModuleId } from './module-labels';
import { NO_MODULES } from './openapi-utils';
import type { ModuleHighlight, SpecTree } from './spec-tree';
import VirtualTree from './VirtualTree';

type SpecTreeViewProps = {
  specTree: SpecTree;
  tree: UseTreeReturnType;
  /** The element that scrolls the tree */
  scrollRef: RefObject<HTMLElement>;
  /** How each node relates to the selected modules, or null when no modules are selected */
  highlights?: ReadonlyMap<string, ModuleHighlight> | null;
  selectedModules?: ReadonlySet<ModuleId>;
};

/** Renders a spec tree of operations, sections and schema properties, with their types and module matches */
function SpecTreeView({
  specTree,
  tree,
  scrollRef,
  highlights = null,
  selectedModules = NO_MODULES,
}: SpecTreeViewProps) {
  return (
    <VirtualTree
      data={specTree.data}
      tree={tree}
      scrollRef={scrollRef}
      levelOffset="md"
      selectOnClick
      renderNode={({ node, expanded, hasChildren, elementProps }) => {
        const info = specTree.nodes.get(node.value);
        if (!info) return null;
        const matched = highlights ? info.ownModules.filter((m) => selectedModules.has(m)).sort(compareModules) : [];

        return (
          <Group
            gap={6}
            wrap="nowrap"
            {...elementProps}
            className={`${elementProps.className} spec-tree-node`}
            data-highlight={highlights?.get(node.value)}
            data-kind={info.kind}
            onClick={(event) => {
              elementProps.onClick(event);
              if (hasChildren && !expanded) tree.expand(node.value);
            }}
          >
            <TreeNodeToggle
              hasChildren={hasChildren}
              expanded={expanded}
              onToggle={() => tree.toggleExpanded(node.value)}
            />
            {info.operation && <MethodBadge method={info.operation.method} size="xs" />}
            <Text
              span
              size="sm"
              truncate
              ff={info.kind === 'property' ? 'monospace' : undefined}
              fw={
                info.kind === 'operation' || info.kind === 'requestBody' || info.kind === 'responses' ? 600 : undefined
              }
              fs={info.kind === 'variant' ? 'italic' : undefined}
            >
              {info.operation ? info.operation.path : info.name}
              {info.required && (
                <Text span c="orange">
                  *
                </Text>
              )}
            </Text>
            {(info.kind === 'property' || info.kind === 'response') && (
              <Text span size="xs" c="dimmed" ff="monospace" style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {info.typeLabel}
              </Text>
            )}
            {matched.length > 0 && (
              <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                {matched.map((m) => (
                  <Badge key={m} size="xs" color="teal" radius="sm" tt="none">
                    {m}
                  </Badge>
                ))}
              </Group>
            )}
          </Group>
        );
      }}
    />
  );
}

export default SpecTreeView;
